import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { locationSummaryQueryOptions } from '@/api/accommodation/hooks'
import type { Image } from '@/api/accommodation/types'
import { unitFullName, unitPhotos } from '@/lib/unit'

function thumbnailUrl(photos: Image[]): string | undefined {
  if (photos.length === 0) return undefined
  return [...photos].sort((a, b) => b.priority - a.priority)[0]?.url.thumbnail
}

/**
 * Live accommodation card embedded in an article via `::nabidka{slug="..."}`.
 * Fetches the current location summary so price/availability stay fresh
 * without editing the article, and links to the location's first unit.
 */
export function ArticleOffer({ slug }: { slug?: string }) {
  const { data, isPending, isError } = useQuery({
    ...locationSummaryQueryOptions(slug ?? ''),
    enabled: Boolean(slug),
  })
  const unit = data?.units[0]

  if (!slug) return null

  return (
    <div className="not-prose my-6">
      {isPending ? (
        <div className="flex gap-4 rounded-xl border border-slate-100 bg-white p-3 shadow-lg shadow-slate-200">
          <div className="h-24 w-32 shrink-0 animate-pulse rounded-lg bg-slate-100" />
          <div className="flex-1 space-y-2 py-2">
            <div className="h-4 w-2/3 animate-pulse rounded bg-slate-100" />
            <div className="h-3 w-1/3 animate-pulse rounded bg-slate-100" />
          </div>
        </div>
      ) : isError || !unit ? null : (
        <Link
          to="/ubytovani/$slug/$unitSlug"
          params={{ slug: data.slug, unitSlug: unit.slug }}
          className="group flex gap-4 rounded-xl border border-slate-100 bg-white p-3 shadow-lg shadow-slate-200 transition-opacity hover:opacity-90"
        >
          {thumbnailUrl(unitPhotos(unit, data)) && (
            <img
              src={thumbnailUrl(unitPhotos(unit, data))}
              alt={unitFullName(data, unit)}
              loading="lazy"
              className="h-24 w-32 shrink-0 rounded-lg object-cover object-center"
            />
          )}
          <div className="flex min-w-0 flex-col justify-center">
            <span className="truncate text-base font-medium text-slate-900">{unitFullName(data, unit)}</span>
            <span className="text-sm text-slate-500">
              {data.address.city}, {data.region.name}
            </span>
            <span className="text-red mt-1 text-sm font-medium">Zobrazit nabídku →</span>
          </div>
        </Link>
      )}
    </div>
  )
}
